import {
  CarrierVisitStatus,
  EquipmentType,
  OperationType,
  WorkInstructionDto,
  WorkInstructionStatus,
  WorkQueueActionType,
  WorkQueueDto,
} from '@operations/app/api'
import { JobUpsertedUpdatedEventPayload } from '@operations/app/api/signalRDtos/JobUpsertedEventPayload'
import { WorkQueueUpsertedEventPayload } from '@operations/app/api/signalRDtos/workQueueUpsertedEventPayload'
import { SelectOption } from '@operations/app/models'
import { JobSignalRDto } from '@operations/features/equipmentOperator/models/job-signalR-event.model'
import { EventTypes } from '@operations/messages/eventsTypes'
import { workInstructionService } from '@operations/services'
import { CarrierVisitStore } from '@operations/stores/CarrierVisitStore'
import { EquipmentStore } from '@operations/stores/EquipmentStore'
import { WorkInstructionStore } from '@operations/stores/WorkInstructionStore'
import { WorkQueueStore } from '@operations/stores/WorkQueueStore'
import { carrierVisitOnTerminalStatus } from '@operations/utils/helper'
import { appStore, IEvent, MessageBus, PATHS } from '@tom-ui/utils'
import dayjs from 'dayjs'
import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { SplitFormProfile } from '../components/SplitForm/split-form.profile'
import { WorkQueueFormProfile } from '../components/WorkQueueForm/workqueue-form.profile'
import { WorkQueue, WorkQueueStackChange } from '../models/work-queue.model'
import { calculateEndTime, CraneSplitContainerUIStore } from './crane-split-container.ui-store'

export class CraneSplitDialogUIStore {
  vesselId?: number
  workQueueId?: number
  workQueue?: WorkQueue
  processingTimeIsOpen = false
  editWorkQueueIsOpen = false
  splitIsOpen = false
  signalREventsQueue: JobSignalRDto[] = []

  removeDialog = false
  resetDialog = false
  detailsDialog = false
  startDialog = false
  renameDialog = false
  stopDialog = false

  constructor(
    private messageBus: MessageBus,
    private workQueueStore: WorkQueueStore,
    private equipmentStore: EquipmentStore,
    private craneSplitContainerUIStore: CraneSplitContainerUIStore,
    private workInstructionStore: WorkInstructionStore,
    private carrierVisitStore: CarrierVisitStore,
  ) {
    makeObservable(this, {
      removeDialog: observable,
      resetDialog: observable,
      detailsDialog: observable,
      editWorkQueueIsOpen: observable,
      processingTimeIsOpen: observable,
      startDialog: observable,
      renameDialog: observable,
      splitIsOpen: observable,
      stopDialog: observable,
      workQueueId: observable,
      workQueue: observable,
      vesselId: observable,

      toggleProcessingTime: action,
      toggleEditWorkQueueDrawer: action,
      toggleSplit: action,
      toggleRenameDialog: action,
      toggleResetDialog: action,
      toggleRemoveDialog: action,
      toggleDetailsDialog: action,
      toggleStartDialog: action,
      toggleStopDialog: action,
      setVesselId: action,
      setProcessingTime: action,
      splitWorkQueue: action,
      removeWorkQueue: action,
      renameQueue: action,

      workQueueFromStore: computed,
      vesselVisit: computed,
      isVisitOnTerminal: computed,
      selectedWorkQueueWorkInstructions: computed,
      canStartOrStop: computed,
    })
    this.getWorkInstructionForRestow = this.getWorkInstructionForRestow.bind(this)

    this.messageBus.subscribeEvent<JobUpsertedUpdatedEventPayload>(
      EventTypes.JobUpserted,
      message => this.enqueueSignalREvent(message, this.handleJobUpsertedEvent),
    )

    this.messageBus.subscribeEvent<WorkQueueUpsertedEventPayload>(
      EventTypes.WorkQueueUpserted,
      this.reloadWorkQueueAndWorkInstructions,
    )
  }

  public get selectedWorkQueueWorkInstructions() {
    const workInstructions = this.workInstructionStore.items.filter(
      x => x.workQueueId === this.workQueue?.id,
    )
    return _(workInstructions)
      .orderBy(x => x.workQueueOrder)
      .value()
  }

  handleJobUpsertedEvent = async (res: IEvent<JobUpsertedUpdatedEventPayload>) => {
    if (!res.payload) return

    if (this.workQueue?.carrierVisitId === res.payload.job.carrierVisit?.id) {
      this.workInstructionStore.loadWorkInstructionsByIds([res.payload.job.workInstructionId])
    }
  }

  updateWorkQueue = async (res: IEvent<WorkQueueUpsertedEventPayload>) => {
    const oldItem = this.workQueueStore.items.find(item => item.id === res.payload?.id)
    const workQueue = Object.assign({}, oldItem, res.payload)
    this.workQueueStore.updateStoreItem(workQueue, workQueue.id)

    await this.workInstructionStore.loadWorkInstructionsByCarrierVisitIdAndWorkQueueId(
      workQueue.carrierVisitId,
      workQueue.id,
    )
    // to update just the name of the selected work queue if it is changed
    if (this.workQueue && this.workQueue.name) {
      this.workQueue.name = workQueue.name ?? ''
    }
  }

  reloadWorkQueueAndWorkInstructions = (res: IEvent<WorkQueueUpsertedEventPayload>) => {
    appStore.triggerReloadBySignalR(
      async () => this.updateWorkQueue(res),
      PATHS.visits.vesselVisits.url,
      PATHS.visits.vesselVisits.craneSplit.url,
    )
  }

  public setVesselId(vesselId: number): void {
    if (this.vesselId != vesselId) this.vesselId = vesselId
  }

  public setProcessingTime(processingTime: number) {
    this.craneSplitContainerUIStore.setProcessingTime(processingTime)
  }

  public toggleRemoveDialog(workQueueId?: number) {
    this.workQueueId = workQueueId
    this.removeDialog = !this.removeDialog
  }

  public toggleResetDialog() {
    this.resetDialog = !this.resetDialog
  }

  public toggleProcessingTime() {
    this.processingTimeIsOpen = !this.processingTimeIsOpen
  }

  public toggleEditWorkQueueDrawer(workQueue?: WorkQueue) {
    this.workQueue = workQueue
    this.editWorkQueueIsOpen = !this.editWorkQueueIsOpen
  }

  public toggleSplit(workQueue?: WorkQueue) {
    this.workQueue = workQueue
    this.splitIsOpen = !this.splitIsOpen
  }

  public toggleRenameDialog() {
    this.renameDialog = !this.renameDialog
  }

  public toggleStartDialog(workQueueId?: number) {
    this.workQueueId = workQueueId
    this.startDialog = !this.startDialog
  }

  public toggleStopDialog(workQueueId?: number) {
    this.workQueueId = workQueueId
    this.stopDialog = !this.stopDialog
  }

  public toggleDetailsDialog(workQueue?: WorkQueue) {
    this.workQueue = workQueue
    this.detailsDialog = !this.detailsDialog
  }

  public async renameQueue(id: number, newName?: string) {
    await this.workQueueStore.updateWorkQueueName(id, newName ?? null)
  }

  public async removeWorkQueue() {
    if (this.workQueueId && this.vesselId) {
      const calculatedWorkQueue = this.craneSplitContainerUIStore.calculatedBays
        .flatMap(x => x.workQueues)
        .find(x => x.id === this.workQueueId)

      const previousWorkQueueDto = this.workQueueStore.items.find(
        x => x.id === calculatedWorkQueue?.previousInBayId,
      )

      if (!calculatedWorkQueue || !previousWorkQueueDto) {
        return
      }

      const workQueueWorkInstructions = this.workInstructionStore.items.filter(
        x => x.workQueueId === this.workQueueId,
      )
      const nextWorkQueueWorkInstructionIds =
        this.craneSplitContainerUIStore.getWorkInstructionsIdsByWorkQueueId(previousWorkQueueDto.id)

      const changes: WorkQueueStackChange[] = [
        {
          action: WorkQueueActionType.Edit,
          previous: {
            workQueue: { ...previousWorkQueueDto },
            workInstructionsIds: nextWorkQueueWorkInstructionIds,
          },
        },
        {
          action: WorkQueueActionType.Delete,
          previous: {
            workQueue: { ...this.workQueueStore.items.find(x => x.id === this.workQueueId)! },
            workInstructionsIds: workQueueWorkInstructions.map(wi => wi.id),
          },
        },
      ]

      workQueueWorkInstructions.forEach(x => (x.workQueueId = previousWorkQueueDto.id))

      previousWorkQueueDto.containersAmount += workQueueWorkInstructions.length
      previousWorkQueueDto.moves =
        this.craneSplitContainerUIStore.calculateMoves(previousWorkQueueDto)

      this.workQueueStore.updateStoreItem(previousWorkQueueDto, previousWorkQueueDto.id)

      changes[0].current = {
        workQueue: { ...previousWorkQueueDto },
        workInstructionsIds: [
          ...nextWorkQueueWorkInstructionIds,
          ...workQueueWorkInstructions.map(wi => wi.id),
        ],
      }

      this.workQueueStore.deleteStoreItem(this.workQueueId)

      this.craneSplitContainerUIStore.addWorkQueuesToStack(changes)
    }
  }

  public reset() {
    for (let i = this.craneSplitContainerUIStore.stackPointer - 1; i >= 0; i--) {
      this.craneSplitContainerUIStore.undo()
    }

    this.craneSplitContainerUIStore.clearWorkQueueStack()
  }

  public async editWorkQueue(newWorkQueueData: WorkQueueFormProfile) {
    if (!this.workQueue) {
      return
    }

    const workQueue = this.workQueue
    const workQueueDto = this.workQueueStore.items.find(x => x.id === workQueue.id)

    if (!workQueueDto) {
      return
    }

    const changes: WorkQueueStackChange[] = []
    const change: WorkQueueStackChange = {
      action: WorkQueueActionType.Edit,
      previous: { workQueue: { ...workQueueDto } },
    }

    const containersAmountDiff = workQueue.containersAmount - newWorkQueueData.containersAmount

    workQueueDto.containersAmount = newWorkQueueData.containersAmount
    workQueueDto.isTwinContainers = newWorkQueueData.twinContainers
    workQueueDto.moves = newWorkQueueData.totalMoves
    workQueueDto.startTime = newWorkQueueData.startDate.toString()
    workQueueDto.craneId = +newWorkQueueData.craneId

    this.workQueueStore.updateStoreItem(workQueueDto, +workQueue.id)

    change.current = { workQueue: { ...workQueueDto } }

    changes.push({ ...change })

    let workQueueId = workQueueDto.id
    let updateNext = false

    if (containersAmountDiff > 0) {
      change.previous!.workInstructionsIds =
        this.craneSplitContainerUIStore.getWorkInstructionsIdsByWorkQueueId(+workQueue.id)

      const nextWorkQueue = this.addNewWorkQueue(workQueueDto, containersAmountDiff)
      workQueueId = nextWorkQueue.current!.workQueue.id
      updateNext = true

      change.current.workInstructionsIds =
        this.craneSplitContainerUIStore.getWorkInstructionsIdsByWorkQueueId(+workQueue.id)

      changes.push(nextWorkQueue)
    }

    const bay = this.craneSplitContainerUIStore.calculatedBays.find(x => x.bay === workQueue.bay)
    const bayWorkQueue = bay?.workQueues.find(x => x.id === workQueueId)

    if (!bayWorkQueue) {
      return
    }

    const nextWorkQueues = bay?.workQueues.filter(
      x => x.bay === workQueue.bay && x.order >= bayWorkQueue?.order && x.id != bayWorkQueue?.id,
    )

    const firstNextWorkQueue = _(nextWorkQueues)
      .sortBy(x => x.startTime)
      .first()

    const bayWorkQueueEndDate = dayjs(bayWorkQueue.endDate).set('second', 0)

    if (
      nextWorkQueues &&
      firstNextWorkQueue &&
      (updateNext ||
        bayWorkQueueEndDate.isAfter(dayjs(firstNextWorkQueue.startTime).set('second', 0)))
    ) {
      const firstNextWorkQueueStartTime = dayjs(firstNextWorkQueue.startTime).set('second', 0)

      let lastOrder = bayWorkQueue.order
      let minutesToAdd = 0

      if (bayWorkQueueEndDate.isAfter(firstNextWorkQueueStartTime)) {
        minutesToAdd = dayjs(bayWorkQueue.endDate).diff(
          firstNextWorkQueue.startTime,
          'minute',
          true,
        )
      }

      nextWorkQueues.forEach(nextWorkQueue => {
        const workQueueDto = this.workQueueStore.items.find(x => x.id === nextWorkQueue.id)

        if (workQueueDto) {
          change.previous = { workQueue: { ...workQueueDto } }

          workQueueDto.order = lastOrder + 1
          workQueueDto.startTime = dayjs(workQueueDto.startTime)
            .add(minutesToAdd, 'minute')
            .toString()

          this.workQueueStore.updateStoreItem(workQueueDto, workQueueDto.id)
          change.current = { workQueue: { ...workQueueDto } }
          changes.push({ ...change })

          lastOrder = workQueueDto.order
        }
      })
    }

    this.craneSplitContainerUIStore.addWorkQueuesToStack(changes)
  }

  public splitWorkQueue(splitData: SplitFormProfile) {
    if (this.workQueue) {
      this.editWorkQueue({
        containersAmount: this.workQueue.containersAmount - splitData.containersAmount,
        totalMoves: splitData.totalMoves,
        twinContainers: this.workQueue.isTwinContainers,
        bay: this.workQueue.bay,
        craneId: this.workQueue.craneId,
        operationType: this.workQueue.operationType,
        position: this.workQueue.vesselArea,
        startDate: this.workQueue.startTime,
      })
    }
  }

  public get equipmentOptions(): SelectOption[] {
    return _(
      this.equipmentStore.items
        .filter(eq => eq.equipmentType === EquipmentType.Sts)
        .map(({ id, name }) => ({ label: name, value: id })),
    )
      .sortBy(i => i.label)
      .value()
  }

  public get operationTypeOptions(): SelectOption[] {
    return [
      {
        label: 'Discharge',
        value: OperationType.Inbound,
      },
      {
        label: 'Loading',
        value: OperationType.Outbound,
      },
    ]
  }

  public get processingTime() {
    return this.craneSplitContainerUIStore.processingTime
  }

  public get workQueueDefaultValues(): WorkQueueFormProfile {
    if (!this.workQueue) {
      return {
        bay: '',
        position: '',
        operationType: '',
        containersAmount: 1,
        twinContainers: false,
        totalMoves: 1,
        startDate: new Date(),
        craneId: '',
      }
    }

    return {
      bay: this.workQueue.bay,
      position: this.workQueue.vesselArea,
      operationType: this.workQueue.operationType,
      containersAmount: this.workQueue.containersAmount,
      twinContainers: this.workQueue.isTwinContainers,
      totalMoves: this.workQueue.moves,
      startDate: this.workQueue.startTime,
      craneId: this.workQueue.craneId,
    }
  }

  public get vesselVisit() {
    return this.carrierVisitStore.items.find(vesselVisit => vesselVisit.id === this.vesselId)
  }

  public get isVisitOnTerminal() {
    return (
      this.vesselVisit?.status && carrierVisitOnTerminalStatus.includes(this.vesselVisit.status)
    )
  }

  public get hasWorkQueues() {
    return !!this.workQueueStore.items.length
  }

  public get workQueueFromStore() {
    return this.workQueueStore.items.find(
      x => x.id === this.workQueueId || x.id === this.workQueue?.id,
    )
  }

  getWorkInstructionForRestow(
    containerNumber: string,
    operationType: OperationType,
  ): WorkInstructionDto | undefined {
    return this?.workInstructionStore?.items?.find(
      wi => wi.cargoUnit?.containerNumber === containerNumber && wi.operationType === operationType,
    )
  }

  private addNewWorkQueue(
    baseWorkQueue: WorkQueueDto,
    containersAmount: number,
  ): WorkQueueStackChange {
    const newWorkQueueId = +_(this.workQueueStore.items).maxBy(x => x.id)!.id + 1

    const workQueueWorkInstructions = this.workInstructionStore.items.filter(
      x => x.workQueueId === baseWorkQueue.id && x.status !== WorkInstructionStatus.Finished,
    )

    const newWorkQueueContainers = _(workQueueWorkInstructions)
      .orderBy(x => x.workQueueOrder, 'desc')
      .take(containersAmount)
      .value()

    newWorkQueueContainers.forEach(x => (x.workQueueId = newWorkQueueId))

    const endTime = calculateEndTime(
      dayjs(baseWorkQueue.startTime).toDate(),
      baseWorkQueue.moves,
      baseWorkQueue.processingTimeSeconds,
    )

    const newWorkQueue: WorkQueueDto = {
      ...baseWorkQueue,
      id: newWorkQueueId,
      containersAmount: containersAmount,
      startTime: dayjs(endTime).set('second', 0).toString(),
      order: baseWorkQueue.order + 1,
    }

    newWorkQueue.moves = this.craneSplitContainerUIStore.calculateMoves(newWorkQueue)

    this.workQueueStore.items.push(newWorkQueue)

    return {
      action: WorkQueueActionType.Add,
      current: {
        workQueue: { ...newWorkQueue },
        workInstructionsIds: newWorkQueueContainers.map(x => x.id),
      },
    }
  }

  private enqueueSignalREvent = async (eventMessage: any, eventHandler: any) => {
    this.signalREventsQueue.push({
      eventMessage,
      eventHandler,
    })

    if (this.signalREventsQueue.length === 1) {
      appStore.forbidLoader()

      await this.processSignalRQueue().finally(() => appStore.unforbidLoader())
    }
  }

  private processSignalRQueue = async () => {
    if (!this.signalREventsQueue.length) {
      return
    }

    const firstEvent = this.signalREventsQueue[0]
    await firstEvent.eventHandler(firstEvent.eventMessage)

    this.signalREventsQueue.shift()

    await this.processSignalRQueue()
  }

  get canStartOrStop() {
    return this.vesselVisit?.status === CarrierVisitStatus.InOperation
  }

  public async setPriority(ids: number[], isPriority: boolean) {
    await workInstructionService.setPriority(ids, isPriority)
  }

  public async stopWorkInstructions(workInstructions: WorkInstructionDto[]) {
    await workInstructionService.stop(workInstructions.map(wi => wi.id))
  }

  public async startWorkInstruction(workInstructions: WorkInstructionDto[]) {
    await workInstructionService.start(workInstructions.map(wi => wi.id))
  }
}
